import { z } from 'zod';
import { isoDateSchema } from './squad';

const optionalNumber = (schema: z.ZodNumber) =>
  schema
    .nullish()
    .transform((v) => v ?? undefined)
    .optional();

const optionalText = (max: number) =>
  // Accepts null as well as undefined — see the note in ./match.ts.
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? undefined : v))
    .optional();

const endsAfterStarts = { message: 'ends_before_starts', path: ['endsOn'] };

// ── Game model ─────────────────────────────────────────────────────────

export const phaseSchema = z.enum([
  'with_ball',
  'without_ball',
  'attacking_transition',
  'defensive_transition',
  'set_pieces',
]);

export const gameModelInputSchema = z.object({
  teamId: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(120),
  formation: optionalText(20),
  philosophy: optionalText(4000),
});

export const principleInputSchema = z.object({
  phase: phaseSchema,
  moment: optionalText(40),
  name: z.string().trim().min(1).max(160),
  description: optionalText(2000),
  sort: z.number().int().min(0).max(9999).default(0),
});

// ── Periodization ──────────────────────────────────────────────────────


/**
 * The block plan.
 *
 * Deliberately the same shape for a macrocycle and a mesocycle, because they are
 * the same kind of statement at two scales: not what will be trained, but how
 * what to train will be decided.
 *
 * The five dimension objectives are written into the objective table rather than
 * stored as text, so a block's intentions sit on the same chain as everything
 * below them and can be walked back to from a session in March.
 */
export const cyclePlanSchema = z.object({
  diagnosis: optionalText(2000),
  evidence: optionalText(2000),
  rationale: optionalText(2000),
  /** game_model_principle ids. */
  principleIds: z.array(z.string().min(1).max(64)).max(30).optional(),
  /** physical_quality and energy_system keys. */
  physicalEmphasis: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  successCriteria: optionalText(2000),
  evaluationMethod: optionalText(2000),
  /** The field that keeps a plan alive rather than a prediction. */
  modifyIf: optionalText(2000),
  reviewOn: isoDateSchema.optional(),
  outcome: optionalText(2000),
  /** The team objective this block serves, and one per dimension. */
  objectives: z
    .array(
      z.object({
        dimension: z
          .enum(['team', 'technical', 'tactical', 'physical', 'mental', 'social'])
          .default('team'),
        title: z.string().trim().min(1).max(300),
        detail: optionalText(1000),
      }),
    )
    .max(12)
    .optional(),
});

export type CyclePlan = z.infer<typeof cyclePlanSchema>;

export const macrocycleInputSchema = z
  .object({
    seasonId: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    dominantTheme: optionalText(160),
    secondaryTheme: optionalText(160),
    objectiveSummary: optionalText(1000),
    sort: z.number().int().min(0).max(9999).default(0),
  })
  .extend(cyclePlanSchema.shape)
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

export const mesocycleInputSchema = z
  .object({
    macrocycleId: z.string().min(1).max(64),
    name: z.string().trim().min(1).max(120),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    dominantTheme: optionalText(160),
    secondaryTheme: optionalText(160),
    objectiveSummary: optionalText(1000),
    sort: z.number().int().min(0).max(9999).default(0),
  })
  .extend(cyclePlanSchema.shape)
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

export const microcycleDayInputSchema = z.object({
  onDate: isoDateSchema,
  mdLabel: optionalText(12),
  mainObjective: optionalText(300),
  plannedLoad: optionalNumber(z.number().min(0).max(3000)),
  plannedRpe: optionalNumber(z.number().min(0).max(10)),
  plannedDurationMin: optionalNumber(z.number().min(0).max(300)),
  energySystemKey: optionalText(40),
  physicalEmphasis: optionalText(160),
  tacticalEmphasis: optionalText(160),
  dayType: z.enum(['rest', 'recovery', 'normal', 'high']).optional(),
  notes: optionalText(1000),
});

export const microcycleInputSchema = z
  .object({
    teamId: z.string().min(1).max(64),
    seasonId: z.string().min(1).max(64).optional(),
    mesocycleId: z.string().min(1).max(64).optional(),
    name: z.string().trim().min(1).max(120),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    anchorEventId: z.string().min(1).max(64).optional(),
    objectiveSummary: optionalText(1000),
    /** Why the week changed. Required by the UI when re-planning, not by the schema. */
    adjustmentReason: optionalText(1000),
    days: z.array(microcycleDayInputSchema).max(21).default([]),
  })
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

// ── Sessions ───────────────────────────────────────────────────────────

export const sessionExerciseInputSchema = z.object({
  exerciseId: z.string().min(1).max(64).optional(),
  name: z.string().trim().min(1).max(160),
  categoryKey: optionalText(40),
  durationMin: optionalNumber(z.number().min(0).max(180)),
  sets: optionalNumber(z.number().int().min(1).max(30)),
  reps: optionalNumber(z.number().int().min(1).max(100)),
  workSeconds: optionalNumber(z.number().int().min(1).max(3600)),
  restSeconds: optionalNumber(z.number().int().min(0).max(3600)),
  intensity: optionalNumber(z.number().min(0).max(10)),
  energySystemKey: optionalText(40),
  rationale: optionalText(1000),
  notes: optionalText(1000),
});

export const sessionInputSchema = z.object({
  teamId: z.string().min(1).max(64),
  microcycleId: z.string().min(1).max(64).optional(),
  microcycleDayId: z.string().min(1).max(64).optional(),
  calendarEventId: z.string().min(1).max(64).optional(),
  onDate: isoDateSchema,
  startsAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional().or(z.literal('').transform(() => undefined)),
  title: optionalText(160),
  objective: optionalText(1000),
  matchObjective: optionalText(1000),
  rationale: optionalText(2000),
  plannedDurationMin: optionalNumber(z.number().min(0).max(300)),
  expectedPlayers: optionalNumber(z.number().int().min(0).max(60)),
  status: z.enum(['planned', 'completed', 'cancelled']).default('planned'),
  exercises: z.array(sessionExerciseInputSchema).max(30).default([]),
});

/** What the coach taps after a session: one rating, and who did what. */
export const sessionActualSchema = z.object({
  actualDurationMin: optionalNumber(z.number().min(0).max(300)),
  actualRpe: optionalNumber(z.number().min(0).max(10)),
  actualPlayers: optionalNumber(z.number().int().min(0).max(60)),
  attendance: z
    .array(
      z.object({
        playerId: z.string().min(1).max(64),
        status: z.enum(['present', 'absent', 'late', 'excused', 'injured', 'modified']),
        minutesDone: optionalNumber(z.number().min(0).max(300)),
        note: optionalText(300),
      }),
    )
    .max(80)
    .default([]),
  /** Players the coach flagged with his own eyes. */
  flags: z
    .array(
      z.object({
        playerId: z.string().min(1).max(64),
        kind: z.enum(['tired', 'sore', 'off', 'pain', 'other']),
        note: optionalText(300),
      }),
    )
    .max(80)
    .default([]),
});

export const sessionEvaluationSchema = z.object({
  objectiveAchieved: z.enum(['yes', 'partial', 'no']).optional(),
  why: optionalText(2000),
  whatWorked: optionalText(2000),
  whatDidNot: optionalText(2000),
  playerResponse: optionalText(2000),
  unexpectedIssue: optionalText(2000),
  repeatNext: optionalText(2000),
  changeNext: optionalText(2000),
});

export type GameModelInput = z.infer<typeof gameModelInputSchema>;
export type PrincipleInput = z.infer<typeof principleInputSchema>;
export type MacrocycleInput = z.infer<typeof macrocycleInputSchema>;
export type MesocycleInput = z.infer<typeof mesocycleInputSchema>;
export type MicrocycleInput = z.infer<typeof microcycleInputSchema>;
export type MicrocycleDayInput = z.infer<typeof microcycleDayInputSchema>;
export type SessionInput = z.infer<typeof sessionInputSchema>;
export type SessionExerciseInput = z.infer<typeof sessionExerciseInputSchema>;
export type SessionActualInput = z.infer<typeof sessionActualSchema>;
export type SessionEvaluationInput = z.infer<typeof sessionEvaluationSchema>;

export interface MicrocycleDayRecord extends MicrocycleDayInput {
  id: string;
  sort: number;
}

export interface MicrocycleRecord {
  id: string;
  teamId: string;
  seasonId: string | null;
  mesocycleId: string | null;
  name: string;
  startsOn: string;
  endsOn: string;
  anchorEventId: string | null;
  objectiveSummary: string | null;
  adjustmentReason: string | null;
  days: MicrocycleDayRecord[];
}

export interface SessionRecord {
  id: string;
  teamId: string;
  microcycleId: string | null;
  microcycleDayId: string | null;
  onDate: string;
  startsAt: string | null;
  title: string | null;
  objective: string | null;
  matchObjective: string | null;
  rationale: string | null;
  plannedDurationMin: number | null;
  plannedRpe: number | null;
  plannedLoad: number | null;
  actualDurationMin: number | null;
  actualRpe: number | null;
  actualLoad: number | null;
  expectedPlayers: number | null;
  actualPlayers: number | null;
  status: string;
}

export interface SessionDetail extends SessionRecord {
  exercises: Array<
    SessionExerciseInput & { id: string; sort: number; estimatedRpe: number | null }
  >;
  evaluation: SessionEvaluationInput | null;
}
